import {
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControl,
  Modal,
  ModalDialog,
  Stack,
  Typography,
  Box,
} from "@mui/joy";
import { useEffect, useState } from "react";
import API_SERVICE from "../../../utils/apiService";
import SearchBox from "../../../components/common/SearchBox";
import { useDispatch, useSelector } from "react-redux";
import { setPlaces, setShowCreatePlaceModal } from "../../../store";
import mascotAddWineDetails from "../../../assets/mascot_add-wine-details.png";
import TextInput from "../../common/TextInput";

function CreatePlaceModal() {
  const dispatch = useDispatch();
  const showModal = useSelector((state) => state.modal.showCreatePlaceModal);

  const [chosenPlaceId, setChosenPlaceId] = useState(null);
  const [placeData, setPlaceData] = useState(null);

  // values for new entry in modal
  const [corkageFee, setCorkageFee] = useState(0);
  const [acceptsReservations, setAcceptsReservations] = useState(true);
  const [hasWineGlasses, setHasWineGlasses] = useState(true);
  const [notes, setNotes] = useState(null);
  const [hasBottleLimit, setHasBottleLimit] = useState(false);
  const [bottleLimit, setBottleLimit] = useState(1);

  const [showThankYou, setShowThankYou] = useState(false);

  useEffect(() => {
    if (chosenPlaceId === null) {
      setChosenPlaceId(null);
      setPlaceData(null);
      return;
    }
    const populatePlaceData = async () => {
      const dataResponse =
        await API_SERVICE.fetchSelectedSuggestion(chosenPlaceId);
      setPlaceData(dataResponse);
    };
    populatePlaceData();
  }, [chosenPlaceId]);

  useEffect(() => {
    if (showModal) {
      setShowThankYou(false);
    }
  }, [showModal]);

  const mapBounds = useSelector((state) => state.map.mapBounds);
  const acceptsReservationsFilter = useSelector(
    (state) => state.filter.acceptsReservations,
  );
  const hasWineGlassesFilter = useSelector(
    (state) => state.filter.hasWineGlasses,
  );
  async function resetData() {
    setChosenPlaceId(null);
    setPlaceData(null);
    setCorkageFee(0);
    setAcceptsReservations(true);
    setHasWineGlasses(true);
    setNotes(null);
    setHasBottleLimit(false);
    setBottleLimit(1);
    // refresh the placeData on the map-marker & list-item
    const markerList = await API_SERVICE.searchPlaces(
      mapBounds,
      acceptsReservationsFilter,
      hasWineGlassesFilter,
    );
    dispatch(setPlaces(markerList));
  }

  return (
    <>
      <Modal
        open={showModal}
        onClose={() => {
          setShowThankYou(false);
          dispatch(setShowCreatePlaceModal(false));
        }}
      >
        <ModalDialog sx={{
          overflowY: "auto",
        }}>
          {showThankYou ? (
            <Stack alignItems="center" spacing={4} sx={{ minWidth: 320, minHeight: 200, justifyContent: 'center' }}>
              <Typography level="h3" fontWeight="bold" sx={{ fontSize: 32 }}>Thank You!</Typography>
              <Typography level="body-sm" sx={{ textAlign: 'center', fontSize: 14, color: '#000', lineHeight: 1.2 }}>
                Your valuable insights make us cheer a little<br />
                louder. You deserve an extra pour!
              </Typography>
              <Box component="img" src={mascotAddWineDetails} alt="Mascot cheers" sx={{ width: 120, height: 'auto', my: 1 }} />
              <Button
                onClick={() => {
                  setShowThankYou(false);
                  dispatch(setShowCreatePlaceModal(false));
                }}
                sx={{ mt: 2, borderRadius: '24px', backgroundColor: '#202030', color: '#fff', '&:hover': { backgroundColor: '#F0B27A' } }}
              >
                Close
              </Button>
            </Stack>
          ) : (
            <>
              <DialogTitle>Add Restaurant Details</DialogTitle>
              <DialogContent>
                Search for a restaurant to add details
              </DialogContent>
              <SearchBox
                handleSelectOption={(placeId) => setChosenPlaceId(placeId)}
                placeholder="search restaurant"
              />
              <form
                onSubmit={async (event) => {
                  event.preventDefault();
                  await API_SERVICE.createPlace(
                    chosenPlaceId,
                    corkageFee,
                    acceptsReservations,
                    hasWineGlasses,
                    notes,
                    hasBottleLimit ? bottleLimit : null,
                  );
                  resetData();
                  setShowThankYou(true);
                }}
              >
                {placeData && (
                  <>
                    <Stack spacing={2}>
                      <Stack spacing={-0.5}>
                        <Typography level="body-md" fontWeight="bold">
                          {placeData["name"]}
                        </Typography>
                        <Typography level="body-sm">
                          {placeData["formattedAddress"]}
                        </Typography>
                        <Typography level="body-sm">
                          {placeData["formattedPhoneNumber"]}
                        </Typography>
                      </Stack>
                      <Stack spacing={0}>
                        <Typography level="title-sm" fontWeight={700} sx={{ mb: 0.5 }}>
                          Corkage Fee
                        </Typography>
                        <TextInput
                          value={corkageFee}
                          onChange={(event) => setCorkageFee(event.target.value)}
                          startDecorator={"$"}
                          endDecorator={<Typography level="body-sm">per bottle</Typography>}
                        />
                      </Stack>
                      <FormControl>
                        <Checkbox
                          label="Bottle Limit?"
                          checked={hasBottleLimit}
                          onChange={(_e) => setHasBottleLimit(!hasBottleLimit)}
                        />
                      </FormControl>
                      <Stack spacing={0}>
                        <Typography level="title-sm" fontWeight={700} sx={{ mb: 0.5 }}>
                          Bottle Limit (Optional)
                        </Typography>
                        <TextInput
                          disabled={!hasBottleLimit}
                          value={bottleLimit}
                          onChange={(event) => setBottleLimit(event.target.value)}
                          type="number"
                          min={1}
                        />
                      </Stack>
                      <Stack spacing={0}>
                        <Typography level="title-sm" fontWeight={700} sx={{ mb: 0.5 }}>
                          Corkage Notes (Optional)
                        </Typography>
                        <TextInput
                          value={notes}
                          onChange={(event) => {
                            if (event.target.value === "") {
                              setNotes(null);
                            }
                            setNotes(event.target.value);
                          }}
                        />
                      </Stack>
                      <Stack spacing={1}>
                        <Typography level="title-sm" fontWeight={700} sx={{ mb: 0.5 }}>
                          Other Information
                        </Typography>
                        <Checkbox
                          label="Accepts Reservations"
                          checked={acceptsReservations}
                          onChange={(_e) => setAcceptsReservations(!acceptsReservations)}
                          size="sm"
                          color="neutral"
                        />
                        <Checkbox
                          label="Has Wine Glasses"
                          checked={hasWineGlasses}
                          onChange={(_e) => setHasWineGlasses(!hasWineGlasses)}
                          size="sm"
                          color="neutral"
                        />
                      </Stack>
                      <Button
                        type="submit"
                        sx={{
                          borderRadius: '24px',
                          backgroundColor: '#202030', // COLOR_BLACK
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#F0B27A', // COLOR_PRIMARY
                          },
                        }}
                      >
                        Submit
                      </Button>
                    </Stack>
                  </>
                )}
              </form>
            </>
          )}
        </ModalDialog>
      </Modal>
    </>
  );
}

export default CreatePlaceModal;
